import { useQuery, useMutation, useQueryClient } from "react-query";
import { predictionManagerAxiosApi } from "src/Config/axiosApi";

export interface PairMapperItem {
  _id: string;
  name: string;
  status: string;
  matchedBy: string;
  matchConfidence?: number;
  keywordOverlap?: string[];
  expiryDate: string;
  siteA: {
    platform: string;
    marketId: string;
    marketUrl: string;
  };
  siteB: {
    platform: string;
    marketId: string;
    marketUrl: string;
  };
  createdAt: string;
}

interface PairsResponse {
  pairs: PairMapperItem[];
  total: number;
}

interface PairStats {
  total: number;
  active: number;
  pending: number;
  keywordPending: number;
  expired: number;
}

export function usePairs(params: {
  status?: string;
  matchedBy?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery(
    ["prediction-pairs", params],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<PairsResponse>(
        "/pairs/all",
        { params },
      );
      return data;
    },
    { keepPreviousData: true },
  );
}

export function usePairStats() {
  return useQuery(["prediction-pair-stats"], async () => {
    const { data } =
      await predictionManagerAxiosApi.get<PairStats>("/pairs/stats");
    return data;
  });
}

export function useConfirmPair() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await predictionManagerAxiosApi.patch(`/pairs/${id}/confirm`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
      },
    },
  );
}

export function useRejectPair() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await predictionManagerAxiosApi.patch(`/pairs/${id}/reject`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
      },
    },
  );
}

export function useFetchMarkets() {
  return useMutation(async () => {
    const { data } = await predictionManagerAxiosApi.post("/matching/fetch");
    return data;
  });
}

export function useRunMatching(fullRun = false) {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await predictionManagerAxiosApi.post(
        `/matching/run${fullRun ? '?fullRun=true' : ''}`,
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("matching-runs");
      },
    },
  );
}

export function useBackfillKeywords() {
  return useMutation(async () => {
    const { data } = await predictionManagerAxiosApi.post(
      "/matching/backfill-keywords",
    );
    return data;
  });
}

export interface CachedMarketResult {
  _id: string;
  platform: string;
  externalId: string;
  title: string;
  eventTitle: string;
  keywords: string[];
  isPaired: boolean;
  endDate: string;
  effectiveEndDate?: string;
}

export function useSearchMarkets(
  q: string,
  platform?: string,
  enabled = true,
) {
  return useQuery(
    ["search-markets", q, platform],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<
        CachedMarketResult[]
      >("/pairs/search-markets", {
        params: { q, platform, limit: 20 },
      });
      return data;
    },
    { enabled: enabled && q.length >= 2, keepPreviousData: true },
  );
}

// ─── Matching Run History ────────────────────────────────────────────

export interface MatchDecisionItem {
  sourceExternalId: string;
  sourcePlatform: string;
  sourceTitle: string;
  outcome: "matched" | "no_match" | "no_candidates" | "error";
  matchedExternalId?: string;
  matchedPlatform?: string;
  matchedTitle?: string;
  confidence?: number;
  reasoning?: string;
  keywordOverlap?: string[];
  pairId?: string;
  candidatesConsidered: number;
  errorMessage?: string;
}

export interface MatchingRunItem {
  _id: string;
  startedAt: string;
  completedAt?: string;
  status: string;
  trigger: string;
  newMarketsPolymarket: number;
  newMarketsKalshi: number;
  matchesFound: number;
  matchesCreated: number;
  keywordsExtracted: number;
  errors: string[];
  durationMs: number;
  decisions?: MatchDecisionItem[];
}

export function useMatchingRuns(params: { limit?: number; offset?: number }) {
  return useQuery(
    ["matching-runs", params],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<{
        runs: MatchingRunItem[];
        total: number;
      }>("/matching/runs", { params });
      return data;
    },
    { keepPreviousData: true },
  );
}

export function useMatchingRunDetail(runId: string | null) {
  return useQuery(
    ["matching-run-detail", runId],
    async () => {
      const { data } =
        await predictionManagerAxiosApi.get<MatchingRunItem>(
          `/matching/runs/${runId}`,
        );
      return data;
    },
    { enabled: !!runId },
  );
}

export function useRetryErrors() {
  const qc = useQueryClient();
  return useMutation(
    async (runId: string) => {
      const { data } = await predictionManagerAxiosApi.post(
        `/matching/runs/${runId}/retry-errors`,
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("matching-runs");
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
      },
    },
  );
}

export function useManualPair() {
  const qc = useQueryClient();
  return useMutation(
    async (body: { polymarketId: string; kalshiId: string }) => {
      const { data } = await predictionManagerAxiosApi.post(
        "/pairs/manual-pair",
        body,
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("search-markets");
      },
    },
  );
}
